import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useCurrentSession } from '@/stores/gameSessionStore';
import { useGameStore } from '@/stores/gameStore';
import { createStudentUrl, getStudentRouteForGameState } from '@/utils/studentUrlHelpers';
import { isExitInProgress } from '@/utils/sessionRecovery';
import logger from '@/utils/logger';
import { useSafeNavigate } from './useSafeNavigate';

export const useStudentAutoNavigation = (isEnabled: boolean = true) => {
  const navigate = useSafeNavigate();
  const location = useLocation();
  const currentSession = useCurrentSession();
  const gameState = useGameStore(state => state.gameState);
  const setGameState = useGameStore(state => state.setGameState);
  const prevSessionStateRef = useRef<string | null>(null);

  useEffect(() => {
    // Check if user is on a non-student route (like teacher routes or main lobby without params)
    const isOnTeacherRoute = location.pathname.startsWith('/teacher');
    const isOnMainLobby = location.pathname === '/' && !window.location.search;
    const exitInProgress = isExitInProgress();
    const shouldRespectUserNavigation = isOnTeacherRoute || isOnMainLobby || exitInProgress;

    if (!isEnabled || !currentSession || shouldRespectUserNavigation) {
      return;
    }

    const newSessionState = currentSession.gameState;
    const prevSessionState = prevSessionStateRef.current;
    const targetPath = getStudentRouteForGameState(newSessionState);

    if (!newSessionState) {
      return;
    }

    if (gameState !== newSessionState) {
      setGameState(newSessionState);
    }

    const routeMismatch = Boolean(targetPath && location.pathname !== targetPath);

    // Heal mismatches whenever the route and live session disagree, not only on state changes.
    if (targetPath && (newSessionState !== prevSessionState || routeMismatch)) {
      // Get current URL parameters for consistent navigation
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = currentSession.id;
      const playerId = urlParams.get('playerId');

      if (routeMismatch) {
        logger.debug('Correcting student route to match live session state', {
          fromPath: location.pathname,
          toPath: targetPath,
          previousState: prevSessionState,
          nextState: newSessionState,
          sessionId
        });
        const targetUrl = createStudentUrl(targetPath, sessionId, playerId || undefined);
        navigate(targetUrl);
      }
    }

    // Update the ref to track the latest session state, even if we were already on the correct route.
    prevSessionStateRef.current = newSessionState;
  }, [currentSession?.gameState, currentSession?.id, navigate, location.pathname, setGameState, currentSession, isEnabled, gameState]);

  // Also sync the current question index from the session
  useEffect(() => {
    if (!isEnabled || currentSession?.currentQuestionIndex === undefined) return;

    const gameStore = useGameStore.getState();
    if (gameStore.currentQuestionIndex !== currentSession.currentQuestionIndex) {
      logger.debug('Syncing question index from session', {
        sessionId: currentSession.id,
        questionIndex: currentSession.currentQuestionIndex
      });
      gameStore.syncQuestionIndex(currentSession.currentQuestionIndex);
    }
  }, [isEnabled, currentSession?.currentQuestionIndex, currentSession?.id]);

  return {
    isNavigating: currentSession?.gameState !== gameState,
    sessionState: currentSession?.gameState,
    localState: gameState
  };
};
